import { Injectable } from '@angular/core';
import 'rxjs/add/operator/map';
import { Observable } from 'rxjs/Rx';
import { Subject } from 'rxjs/Subject';
import PouchDB from 'pouchdb';


// Classes.
import { DataContainer, Metadata } from '../db/data-container';
import { DataContainerEmpty } from '../db/data-container-empty';

// Providers.
import { WABaseService } from './wa-base.service';
import { LoggerService } from '../logger/logger-service';
import { EnvironmentService } from './environment-service';


// Interfaces.
import { LocatorInterface } from '../interfaces/locator.interface';
import { of, from } from 'rxjs';
import { tap } from 'rxjs/operators';


@Injectable()
export class DatabaseService extends WABaseService {

  protected storeRequestStream: Subject<any>;
  private _db: any;

  constructor(
    protected logger: LoggerService,
    protected env: EnvironmentService) {
    super(logger, env);
    try {
      this.init();
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * This method is used for make a substitution of the db object due to the firefox issue with indexedDB in private browser.
   * https://bugzilla.mozilla.org/show_bug.cgi?id=781982
   * https://github.com/pouchdb/pouchdb/issues/7401;
   *
   * @returns
   * @memberof DatabaseService
   */
  getAlternativeStorage() {
    return {
      get: (id) => {
        return of(JSON.parse(localStorage.getItem(id))).toPromise();
      },
      destroy: () => {
        localStorage.clear();
      },
      isLocalStorage() {
        return true;
      },
      put: (key, value) => {
        return new Promise((resolve) => {
          localStorage.setItem(key, JSON.stringify(value));
          resolve();
        });
      }
    };
  }

  public init() {
    this._db = new PouchDB('wa');
    if (localStorage.getItem('clear-1') == null) {
      this._db.allDocs().then((result) => {
        return Promise.all(result.rows.map((row) => {
          return this._db.remove(row.id, row.value.rev);
        }));
      }).then(function () {

      }).catch(function (err) {

      });
      localStorage.setItem('clear-1', 'ok');
    }
  }

  public truncate() {
    this._db.destroy();
    this.init();
  }

  public get(locator: LocatorInterface): Observable<DataContainer<any>> {
    let id = locator.getId();
    this.debug(`Trying to recover document with id «${id}»`);
    return Observable.fromPromise(this._db.get(id))
      .map((rawDC: any) => {
        if (typeof rawDC.expires !== 'number') {
          rawDC.expires = 1200;
        }
        let dataContainer = this.rawDataContainerToDataContainer(locator, rawDC);
        this.debug(`Document with id «${id}» found: `, dataContainer);
        return dataContainer;
      })
      .catch(() => {
        this.debug(`Document with id «${id}» not found, returning an empty document`)
        return Observable.of(new DataContainerEmpty(locator))
      });
  }

  protected rawDataContainerToDataContainer(locator: LocatorInterface, rawDC: any): DataContainer<any> {
    let dataContainer = new DataContainer<any>(locator, rawDC.payload, new Metadata(rawDC.metadata.totalItems), rawDC.maxAge);
    dataContainer.setTimestamp(rawDC.timestamp);
    dataContainer.updateExpire(rawDC.expires);
    return dataContainer;
  }

  public store(dataContainer: DataContainer<any>): Observable<DataContainer<any>> {
    let locator = dataContainer.getLocator();
    let id = locator.getId();
    let db = this._db;
    this.debug(`Trying to store document with id «${id}» and data: `, dataContainer);
    return Observable.fromPromise(db.get(locator.getId()))
      .map((storedDoc: any) => {
        this.debug(`Document id «${id}» found, doing an update fololwing data`);
        if (storedDoc) {
          dataContainer.setRevision(storedDoc._rev);
        }
        return Observable.fromPromise(
          db.isLocalStorage && db.isLocalStorage() ?
            db.put(locator.getId(), dataContainer) :
            db.put(dataContainer)
              // Errors come from a duplicate insertion, ignore them as last insertion.
              // would have all data.
              .catch(() => false))
          .catch(err => {
            this.error('Puting into database. Locator and error ->', locator, err);
            return Observable.of(new DataContainerEmpty(locator));
          });
      })
      .catch((reason: any) => {
        return this.processMiss(locator, dataContainer, reason);
      })
      // Return always the constructed DataContainer.
      .map(() => dataContainer);
  }

  protected processMiss(locator: LocatorInterface, dataContainer: DataContainer<any>, reason: any): Observable<DataContainer<any>> {
    let db = this._db;
    if (reason.message && reason.message === 'missing') {
      // Miss, so this is a new insert.
      this.debug(`Document id «${locator.getId()}» not found, doing an insert`);
      return Observable.of(
        db.isLocalStorage && db.isLocalStorage() ?
          db.put(locator.getId(), dataContainer) :
          db.put(dataContainer))          // Errors come from a duplicate insertion, ignore them as last insertion.
        // would have all data.
        .catch(() => of(false))
    } else {
      this.error('Error inserting in database. Error received: ', reason);
      if (reason.name === 'indexed_db_went_bad') {
        this._db = this.getAlternativeStorage();
        reason.message = 'missing'
        this.processMiss(locator, dataContainer, reason)
      }

    }
  }

  public remove(id: string): Observable<any> {
    return from(this._db.get(id)).pipe(tap(doc => this._db.remove(id)));
  }

  public getInfo(): Observable<any> {
    return Observable.fromPromise(this._db.info());
  }

  public getVersion(): string {
    return PouchDB.version;
  }
}
